import React from 'react';
import { MdClose } from 'react-icons/md';
import type { NotificationDto } from '@api/notifications';
import { formatRelativeTime } from '@utils/relativeTime';
import {
  Body,
  BodyText,
  ContentButton,
  DeleteButton,
  ReadDotPlaceholder,
  Row,
  TimestampText,
  Title,
  UnreadDot,
} from './NotificationItem.styled';

interface NotificationItemProps {
  notification: NotificationDto;
  onClick: () => void;
  /**
   * If provided, renders an "X" button on the right that calls this handler.
   * Hidden by default; revealed on row hover / focus.
   */
  onDelete?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onDelete,
}) => {
  const unread = notification.readAt === null;

  return (
    <Row $unread={unread} data-testid={`notification-item__${notification.id}`}>
      <ContentButton
        type="button"
        onClick={onClick}
        data-testid={`notification-item__${notification.id}__open`}
      >
        {unread ? (
          <UnreadDot aria-label="Unread" />
        ) : (
          <ReadDotPlaceholder aria-hidden="true" />
        )}
        <Body>
          <Title $unread={unread}>{notification.title}</Title>
          {notification.body && <BodyText>{notification.body}</BodyText>}
          <TimestampText>{formatRelativeTime(notification.createdAt)}</TimestampText>
        </Body>
      </ContentButton>
      {onDelete && (
        <DeleteButton
          type="button"
          data-role="delete-notification"
          aria-label={`Delete notification: ${notification.title}`}
          data-testid={`notification-item__${notification.id}__delete`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <MdClose aria-hidden="true" />
        </DeleteButton>
      )}
    </Row>
  );
};

export default NotificationItem;
